import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Kind = {
    Firm: "firm",
    Personal: "personal",
    Other: "other"
};
export type KindType = typeof Kind[keyof typeof Kind];
export const FirmLevel = {
    Editor: "editor",
    Viewer: "viewer",
    FreeBusy: "freeBusy"
};
export type FirmLevelType = typeof FirmLevel[keyof typeof FirmLevel];
class Calendar extends Base implements EntityType {
    calendarId: uuid;
    name: string;
    kind: KindType;
    ownerId?: uuid | null;
    description?: string | null;
    firmLevel?: FirmLevelType | null;
    constructor(doc: typeof Calendar | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            calendarId: genUUID(),
            name: null,
            kind: "other",
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "calendar";
        // @ts-ignore
        this.calendarId = doc._calendarId || doc.calendarId || doc.calendarid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.kind = doc._kind || doc.kind || "other";
        // @ts-ignore
        this.ownerId = doc._ownerId || doc.ownerId || doc.ownerid || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.firmLevel = doc._firmLevel || doc.firmLevel || doc.firmlevel || null;
    }
    get id(): uuid { return this.calendarId; }
    static KIND_VALUES = ["firm", "personal", "other"];
    static FIRM_LEVEL_VALUES = ["editor", "viewer", "freeBusy"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for the calendar"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.calendarId; }
    displayFn() { return `${this.name}`; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): Calendar[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'calendar',
    dataArea: 'Calendar',
    table: 'CALENDAR',
    comment: `A named collection of Calendar Entries that Firm Users view and share. Every firm has one Firm Calendar (kind firm) and every Firm User has one Personal Calendar (kind personal, with ownerId set to that user); Firm Users may create more (kind other). Which Firm Users may see or edit a Calendar is set by firmLevel, for everyone in the firm, and by CalendarShare records, for named Firm Users. A Sharing Level limits detail and is not access control: Case Team, Restricted Case and Ethical Wall rules apply on top of it.`,
    fields: {
        calendarId: {
            type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for the calendar'
        },
        name: {
            type: 'string', required: true, maxLength: 128, comment: 'The name of the calendar'
        },
        kind: {
            type: 'string',
            required: true,
            values: ['firm', 'personal', 'other'],
            default: 'other',
            comment: 'firm for the one Firm Calendar, personal for a Firm User\'s own Calendar, other for any Calendar a Firm User created'
        },
        ownerId: {
            type: 'uuid',
            entityType: 'user',
            comment: 'The Firm User who owns a personal or other Calendar and always has Editor access to it; empty for the Firm Calendar'
        },
        description: {
            type: 'string', comment: 'Short description of what the calendar is for'
        },
        firmLevel: {
            type: 'string',
            values: ['editor', 'viewer', 'freeBusy'],
            comment: 'The Sharing Level every Firm User has on this Calendar unless a CalendarShare names them; empty means not shared with the rest of the firm'
        }
    },
    // No generic REST routes: they would list any Calendar and let a caller create an Editor share
    // for themselves. Hand-written routes arrive with the Access Model (OWA-1316).
    restRoutes: false,
    displayFn: "`${this.name}`",
    sortOrder: [
        {position: 1, fieldName: 'name', asc: true}
    ],
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'}
    ],
    maps: [
        {name: 'ownerId', keysFn: t => t.ownerId, presentFn: (t, k) => t.ownerId === k && t.status !== 'D'}
    ]
});
    }
}
export default Calendar;
